import { makeAutoObservable } from "mobx";
import { uuid } from "../../services/helpers";


interface IError {
    title?: string;
    id?: string;
    code?: number;
    message?: string;
    error?: boolean|string;
    file?: string;
    line?: number;
    url?: string;
    external_api_error?: boolean;
}

class ErrorModalStore {
    private _list: ErrorStore[] = [];

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private remove(storeToRemove: ErrorStore):void {
        const index = this._list.findIndex(s => s === storeToRemove);
        if (index === -1) {
            /** the method is called more than once, so in next calls index will not be found */
            return;
        }
        this._list.splice(index, 1);
    }

    public add(err:IError):void {
        err.id = uuid();
        const errStore = new ErrorStore(err, this.remove);
        this._list.unshift(errStore);
    }

    public get list():ErrorStore[] {
        return this._list;
    }
    
}

export default new ErrorModalStore();


/** animation states */
const STATES = [
    { value: 'entering', time: 100 },
    { value: 'visible', time: 4000 },
    { value: 'leaving', time: 3000 },
    { value: 'invisible', time: 500 },
    { value: 'removed' },
];

export class ErrorStore {
    private _timeout: number = 0;
    private _stateIndex: number = -1;

    constructor(
        private _model:IError, 
        private _remove:(store:ErrorStore) => void) { 

        makeAutoObservable(this, {}, { autoBind: true });
        this.advanceState();
    }

    private advanceState() {
        this._stateIndex += 1;
        if (this._stateIndex >= STATES.length) {
            console.error('ErrorStore.advanceState: invalid state index', this._stateIndex);
        }

        const state = STATES[this._stateIndex];
        if (state?.time) {
            this._timeout = window.setTimeout(() => {
                this.advanceState();
            }, state.time);
        }
        else {
            /** the last remove state has no time */
            this.remove();
        }
    }

    public remove():void {
        this._reset();
        this._remove(this);
    }

    private _reset() {
        clearTimeout(this._timeout);
        this._timeout = 0;
    }

    public stop() {
        this._reset();
        this._stateIndex = 1;
    }

    public start() {
        this._reset();
        this._stateIndex = 0;
        this.advanceState();
    }


    public get state():string {
        return STATES[this._stateIndex]?.value;
    }

    public get title() { return this._model.title; }
    public get id() { return this._model.id; }
    public get code() { return this._model.code; }
    public get file() { return this._model.file; }
    public get line() { return this._model.line; }
    public get url() { return this._model.url; }
    public get external_api_error() { return this._model.external_api_error; }

    public get message() { 
        if (typeof this._model.error === 'string') {
            return this._model.error + (this._model.message ? `, ${this._model.message}` : ''); 
        }
        return this._model.message;
    }
}

